import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  EmailField,
  BooleanField,
  DateField,
  TopToolbar,
  EditButton,
  DeleteButton,
  useRecordContext,
  ListButton,
} from 'react-admin';
import {
  Grid,
  Typography,
  Box,
  Avatar,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import {
  Person,
  SecurityOutlined,
  Info as InfoIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

const RolesField = () => {
  const record = useRecordContext();
  if (!record || !record.roles) {
    return null;
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {record.roles.map((role: string) => (
        <Chip key={role} label={role} />
      ))}
    </Box>
  );
};

const ShowTitle = () => {
  const record = useRecordContext();
  return (
    <span>
      {record
        ? `Usuario: ${record.fullName || record.userName}`
        : 'Cargando...'}
    </span>
  );
};

const UserShowActions = () => (
  <TopToolbar>
    <EditButton />
    <DeleteButton />
    <ListButton />
  </TopToolbar>
);

export const UsersShow: React.FC = () => (
  <Show title={<ShowTitle />} actions={<UserShowActions />}>
    <Box sx={{ margin: 'auto', padding: 2 }}>
      <Grid container spacing={3}>
        {/* Información Personal */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 340 }}>
            <CardContent>
              <Box display='flex' alignItems='center' mb={2}>
                <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant='h6' component='h2'>
                  Información Personal
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {/* Avatar y nombre principal */}
              <Box display='flex' alignItems='center' mb={3}>
                <Avatar
                  sx={{ width: 56, height: 56, mr: 2, bgcolor: 'primary.main' }}
                >
                  <Person fontSize='large' />
                </Avatar>
                <Box>
                  <Typography variant='h6' component='div'>
                    <TextField source='fullName' emptyText='Sin nombre' />
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    <TextField source='userName' label='' />
                  </Typography>
                </Box>
              </Box>

              <SimpleShowLayout>
                <TextField source='id' label='ID de Usuario' />
                <TextField source='fullName' label='Nombre Completo' />
                <TextField source='userName' label='Nombre de Usuario' />
              </SimpleShowLayout>
            </CardContent>
          </Card>
        </Grid>

        {/* Información de Contacto */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 340 }}>
            <CardContent>
              <Box display='flex' alignItems='center' mb={2}>
                <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant='h6' component='h2'>
                  Información de Contacto
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <SimpleShowLayout>
                <EmailField source='email' label='Correo Electrónico' />
                <TextField
                  source='phoneNumber'
                  label='Teléfono'
                  emptyText='No especificado'
                />
              </SimpleShowLayout>
            </CardContent>
          </Card>
        </Grid>

        {/* Roles Asignados */}
        <Grid item xs={12}>
          <Card sx={{ height: 340 }}>
            <CardContent>
              <Box display='flex' alignItems='center' mb={2}>
                <SecurityOutlined sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant='h6' component='h2'>
                  Roles Asignados
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <RolesField />
              <Box sx={{ mt: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                  Los roles determinan los permisos y niveles de acceso del
                  usuario en el sistema.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Información del Sistema */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display='flex' alignItems='center' mb={2}>
                <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant='h6' component='h2'>
                  Información del Sistema
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <SimpleShowLayout>
                    <DateField
                      source='createdAt'
                      label='Fecha de Registro'
                      showTime
                      emptyText='No disponible'
                    />
                  </SimpleShowLayout>
                </Grid>
                <Grid item xs={12} md={4}>
                  <SimpleShowLayout>
                    <DateField
                      source='lastLogin'
                      label='Último Acceso'
                      showTime
                      emptyText='Nunca'
                    />
                  </SimpleShowLayout>
                </Grid>
                <Grid item xs={12} md={4}>
                  <SimpleShowLayout>
                    <BooleanField source='isActive' label='Usuario Activo' />
                  </SimpleShowLayout>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                  Usuario registrado en el sistema Inoelec Dashboard. Estado
                  activo permite el acceso completo a las funcionalidades
                  autorizadas.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  </Show>
);
